import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, CalendarClock, Info, Play, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import type { ScenarioCalendarBasis } from "@opus-dt/shared-types";
import type { SandboxPeriod } from "@/types/sim-sandbox.types";
import { ObjectiveChip } from "@/features/simulations/objective/ObjectiveChip";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import { ProductSelect } from "./ProductSelect";
import { operatingHoursLabel } from "./calendar/hours-label";
import { CalendarBasisPill } from "./calendar/CalendarBasisPill";

interface Props {
  scenarioName: string;
  period: SandboxPeriod;
  productId: string | null;
  onProduct: (id: string) => void;
  productReadOnly: boolean;
  onOpenPeriod: () => void;
  onOpenCalendar: () => void;
  /** Horas corridas do período (p/ o rótulo "Nh corridas · Mh operacionais"). */
  runningHours: number | null;
  /** Horas operacionais (∩ calendário); null ⇒ 24/7 (só "Nh"). */
  effectiveHours: number | null;
  /** Base de expediente em uso; null enquanto o servidor não respondeu. */
  calendarBasis: ScenarioCalendarBasis | null;
  onSave: () => void;
  /** Ação ÚNICA de simular (substitui Rodar + Sugerir melhorias). */
  onSimulate: () => void;
  objective: SimulationObjectiveChoice | null;
  onObjective: (next: SimulationObjectiveChoice) => void;
  /** Linha base do cenário — o objetivo de prazo precisa dela para listar as ordens. */
  baseLineId: string | null;
  canRun: boolean;
  isRunning: boolean;
  isSaving: boolean;
}

function periodLabel(p: SandboxPeriod): string {
  if (p.mode === "FREE") {
    if (p.periodStart && p.periodEnd) {
      const f = (iso: string) =>
        new Date(iso).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      return `${f(p.periodStart)} → ${f(p.periodEnd)}`;
    }
    return "Definir período";
  }
  return p.sourceProductionOrderId ? "Ordem de Produção" : "Escolher OP";
}

const KIND: Record<SandboxPeriod["mode"], string> = {
  FREE: "Intervalo",
  OP_PLANNED: "OP planejada",
  OP_EXECUTED: "OP executada",
};

// Barra do editor do sandbox: voltar ao hub, nome do cenário, período
// (drawer), produto e as ações.
// "Rodar" e "Sugerir melhorias" viraram UM botão Simular, e o objetivo ganhou um chip
// sempre visível. As duas ações antigas pareciam a mesma coisa e tinham regras diferentes (uma
// salvava e executava; a outra exigia o cenário já salvo e recusava com aviso), sem nada na tela
// explicando a diferença.
export function SandboxToolbar(props: Props) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2">
      <Button
        variant="ghost"
        size="sm"
        className="px-2"
        aria-label="Voltar ao hub"
        onClick={() => navigate("/simulations")}
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <Pill tone="brand">Sandbox</Pill>
      <span className="max-w-[16rem] truncate text-sm font-semibold" title={props.scenarioName}>
        {props.scenarioName}
      </span>

      <Button variant="secondary" size="sm" onClick={props.onOpenPeriod}>
        <Calendar className="h-4 w-4" />
        <span className="font-mono text-[11px]">{periodLabel(props.period)}</span>
        <span className="rounded bg-muted px-1 text-[9px] font-medium uppercase tracking-wide">
          {KIND[props.period.mode]}
        </span>
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={props.onOpenCalendar}
        title="Calendário deste cenário (herdar / 24 horas / cópia)"
      >
        <CalendarClock className="h-4 w-4" />
        Calendário
      </Button>
      {/* `null` só enquanto o servidor não respondeu: calar é melhor
          que chutar "contínuo", o rótulo mais caro de errar. */}
      {props.calendarBasis ? <CalendarBasisPill basis={props.calendarBasis} /> : null}
      <span
        className="text-[11px] text-muted-foreground"
        title="Horas do período (corridas × operacionais quando há expediente)"
      >
        {operatingHoursLabel(props.runningHours, props.effectiveHours)}
      </span>

      <ProductSelect
        value={props.productId}
        onChange={props.onProduct}
        readOnly={props.productReadOnly}
        compact
      />

      <div className="flex-1" />

      <span className="hidden items-center gap-1 text-[11px] text-muted-foreground lg:flex">
        <Info className="h-3 w-3" />
        Cópia isolada — nada toca a linha real
      </span>

      <ObjectiveChip
        value={props.objective}
        onChange={props.onObjective}
        lineId={props.baseLineId}
      />
      <Button variant="secondary" size="sm" onClick={props.onSave} disabled={props.isSaving}>
        <Save className="h-4 w-4" />
        {props.isSaving ? "Salvando…" : "Salvar cenário"}
      </Button>
      <Button size="sm" onClick={props.onSimulate} disabled={!props.canRun || props.isRunning}>
        <Play className="h-4 w-4" />
        {props.isRunning ? "Simulando…" : "Simular"}
      </Button>
    </div>
  );
}
